import { fetchData, postData, patchData } from '../client';
import type {
  SyncContactsResponse,
  GetContactsResponse,
  ContactWithGroups,
  GroupMemberResponse,
} from '@/types/contacts';
import type {
  PaginationParams,
  GetDMConversationsResponse,
  GetDMMessagesResponse,
} from '@/types/dm';

export const contactsApi = {
  getContacts: async (params?: {
    page?: number;
    perPage?: number;
    search?: string;
    sort?: { attribute: string | null; order: 'asc' | 'desc' };
    contactType?: 'live' | 'exported';
    groupIds?: string[];
  }): Promise<GetContactsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.perPage) queryParams.append('limit', params.perPage.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sort?.attribute)
      queryParams.append('sortAttribute', params.sort.attribute);
    if (params?.sort?.attribute)
      queryParams.append('sortOrder', params.sort.order);
    if (params?.contactType)
      queryParams.append('contactType', params.contactType);
    if (params?.groupIds && params.groupIds.length > 0)
      queryParams.append('groupIds', params.groupIds.join(','));

    const path = queryParams.toString()
      ? `/api/contacts?${queryParams.toString()}`
      : '/api/contacts';

    return fetchData<GetContactsResponse>({
      path,
    });
  },

  getGroupMember: async (groupId: string): Promise<GroupMemberResponse[]> => {
    return fetchData({
      path: `/api/group-members/${groupId}`,
    });
  },

  syncContacts: async (): Promise<SyncContactsResponse> => {
    return postData<SyncContactsResponse>({
      path: '/api/contacts/sync',
    });
  },

  updateContact: async (
    contactId: string,
    data: Partial<ContactWithGroups>,
  ): Promise<{ success: boolean; message: string }> => {
    const requestBody: Record<string, string> = {};

    if (data.contact?.displayName?.trim()) {
      requestBody.displayName = data.contact.displayName.trim();
    }

    if (data.contact?.phoneNumber) {
      const trimmed = data.contact.phoneNumber.trim();
      if (trimmed) {
        requestBody.phoneNumber = trimmed;
      }
    }

    return patchData<{ success: boolean; message: string }>({
      path: `/api/contacts/${contactId}`,
      body: requestBody,
    });
  },

  getDMConversations: async (
    params?: PaginationParams & { search?: string },
  ): Promise<GetDMConversationsResponse> => {
    return fetchData<GetDMConversationsResponse>({
      path: '/api/contacts/dm',
      params: params
        ? {
            ...(params.limit && { limit: params.limit.toString() }),
            ...(params.page && { page: params.page.toString() }),
            ...(params.search && { search: params.search }),
          }
        : undefined,
    });
  },

  getDMMessages: async (
    contactId: string,
    params?: PaginationParams & { sort?: string },
  ): Promise<GetDMMessagesResponse> => {
    return fetchData<GetDMMessagesResponse>({
      path: `/api/contacts/dm/${contactId}/messages`,
      params: params
        ? {
            ...(params.limit && { limit: params.limit.toString() }),
            ...(params.page && { page: params.page.toString() }),
            ...(params.sort && { sort: params.sort }),
          }
        : undefined,
    });
  },

  getDMMessage: async (messageId: string, bustCache = false) => {
    const params = bustCache ? { _t: Date.now().toString() } : undefined;
    return fetchData({
      path: `/api/messages/message/${messageId}`,
      params,
    });
  },

  batchGetDMMessages: async (messageIds: string[]) => {
    return postData({
      path: '/api/messages/batch',
      body: { messageIds },
    });
  },
};
